import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../../components/Header';
import Footer from '../../components/Footer';
import Modal from '../../components/Career/Modal';

const CareerDetailPage = () => {
  const [postDetails, setPostDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPostDetails = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/careerPost/${id}`);
        setPostDetails(response.data);
      } catch (err) {
        console.error("Error fetching career post details:", err);
      } finally {
        setLoading(false); // Ensures flicker is gone
      }
    };

    if (id) {
      fetchPostDetails();
    }
  }, [id]);

  const onBack = () => navigate('/career');

  // Show nothing while loading to prevent flicker
  if (loading) return null;

  // If data not found
  if (!postDetails) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 text-lg text-gray-600 flex-col">
        <p>Career post not found.</p>
        <button
          onClick={onBack}
          className="mt-4 px-4 py-2 bg-[#173d73] text-white rounded-md hover:bg-red-600 transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  const { job, experience, type, description, skills, qualification, location, additionalInfo, image } = postDetails;

  return (
    <>
      <Navbar diffHeader={true} />
      <div className="min-h-screen mt-[50px] bg-gray-50 font-sans text-gray-900 flex flex-col items-center py-16 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-4xl bg-white rounded-xl shadow-lg p-8 md:p-10">
          <button
            onClick={onBack}
            className="mb-8 flex items-center text-[#173d73] font-medium hover:text-red-600 transition-colors duration-200"
            aria-label="Back to Vacancies"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="m15 18-6-6 6-6" /></svg>
            Back to Vacancies
          </button>

          <div className="text-center mb-10">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800 mb-2">
              {job}
            </h1>
            <p className="text-lg text-gray-600 flex items-center justify-center">
``              {location}
            </p>
          </div>

          {image && (
            <div className="mb-8 rounded-lg overflow-hidden shadow-md">
              <img
                src={image}
                alt={job}
                className="w-full h-auto object-cover max-h-96"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://placehold.co/800x450/CCCCCC/333333?text=Image+Not+Available";
                }}
              />
            </div>
          )}

          <div className="space-y-6 text-gray-700 leading-relaxed text-left">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">Job ID</h2>
              <div className='mb-2'>{id}</div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">Job Description</h2>
              {description && description.split('\n').map((paragraph, index) => (
                <p key={index} className={index > 0 ? "mt-2" : ""}>{paragraph}</p>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-3">Key Details</h2>
                <ul className="list-disc list-inside space-y-2">
                  <li><strong>Experience:</strong> {experience}</li>
                  <li><strong>Employment Type:</strong> {type}</li>
                  <li><strong>Qualification:</strong> {qualification}</li>
                </ul>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-3">Skills Required</h2>
                <ul className="list-disc list-inside space-y-2">
                  {skills && skills.split(',').map((skill, index) => (
                    <li key={index}>{skill.trim()}</li>
                  ))}
                </ul>
              </div>
            </div>

            {additionalInfo && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-3">Additional Information</h2>
                {additionalInfo.split('\n').map((paragraph, index) => (
                  <p key={index} className={index > 0 ? "mt-2" : ""}>{paragraph}</p>
                ))}
              </div>
            )}
          </div>

          <div className="text-center mt-10">
            <div
              onClick={() => setShowModal(true)}
              className="inline-block bg-[#173d73] text-white font-semibold py-3 px-8 rounded-full shadow-lg hover:bg-red-600 transition-colors duration-300 text-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
            >
              Apply Now
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex justify-center items-center px-4">
          <div className="bg-white p-6 rounded-lg w-full max-w-xl relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-2 right-3 text-2xl text-gray-500 hover:text-black"
            >
              &times;
            </button>
            <Modal positionVal={[id]} />
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default CareerDetailPage;
